import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, MapPinned } from "lucide-react";
import { ImageLightbox } from "./ImageLightbox";

/**
 * VenueDetailsModal (self-contained UI state)
 *
 * Parent controls:
 * - open (boolean)
 * - onOpenChange(open:boolean)
 * - selectedVenue (object|null)
 * - venueTypeColors (Record<string,string>)
 * - onViewOnMap(venue)
 *
 * This component controls its own UI-only state:
 * - showAllDetails
 * - lightboxOpen
 * - zoomLevel
 */
export function VenueDetailsModal({
  open,
  onOpenChange,
  selectedVenue,
  venueTypeColors,
  onViewOnMap,
}) {
  const [showAllDetails, setShowAllDetails] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  // A stable "id" for reset behavior (prefer venue_name as you used it as ID)
  const venueId = selectedVenue?.venue_name ?? null;

  const badgeColor = useMemo(() => {
    if (!selectedVenue) return venueTypeColors?.["Other"];
    return (
      venueTypeColors?.[selectedVenue.venue_type] ||
      venueTypeColors?.["Other"]
    );
  }, [selectedVenue, venueTypeColors]);

  const closeLightbox = () => {
    setLightboxOpen(false);
    setZoomLevel(1);
  };

  const resetUiState = () => {
    setShowAllDetails(false);
    closeLightbox();
  };

  // Reset UI state when modal closes
  useEffect(() => {
    if (!open) resetUiState();
  }, [open]);

  // Reset UI state when venue changes (e.g. click another marker while modal stays open)
  useEffect(() => {
    if (!venueId) return;
    setShowAllDetails(false);
    closeLightbox();
  }, [venueId]);

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          onOpenChange(nextOpen);
          if (!nextOpen) resetUiState();
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedVenue && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-gray-900">
                  {selectedVenue.store_name || selectedVenue.venue_name}
                </DialogTitle>

                <DialogDescription>
                  <span>
                    <Badge
                      style={{
                        backgroundColor: badgeColor,
                        color: "#ffffff",
                        marginTop: "8px",
                      }}
                    >
                      {selectedVenue.venue_type}
                    </Badge>
                  </span>
                  <span className="ml-2 text-sm text-gray-500">
                    Venue ID: {selectedVenue.venue_name}
                  </span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Image preview */}
                {selectedVenue.image_url && (
                  <div className="relative rounded-lg overflow-hidden border-2 border-gray-200">
                    <img
                      src={selectedVenue.image_url}
                      alt={selectedVenue.venue_name}
                      className="w-full h-auto"
                      loading="lazy"
                    />

                    <div className="absolute top-2 right-2 flex gap-2">
                      <button
                        onClick={() => setLightboxOpen(true)}
                        className="bg-white bg-opacity-90 hover:bg-opacity-100 p-2 rounded-lg shadow-lg transition-all flex items-center justify-center"
                        title="View Fullscreen"
                        type="button"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Primary Information - Always Visible */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <MapPin size={18} className="mr-2 text-blue-600" />
                      Location
                    </h3>

                    <div className="space-y-2 text-sm">
                      <p>
                        <strong className="text-gray-700">Address:</strong>{" "}
                        <span className="text-gray-600">
                          {selectedVenue.address}
                        </span>
                      </p>
                      <p>
                        <strong className="text-gray-700">City:</strong>{" "}
                        <span className="text-gray-600">
                          {selectedVenue.city}
                        </span>
                      </p>
                      <p>
                        <strong className="text-gray-700">State:</strong>{" "}
                        <span className="text-gray-600">
                          {selectedVenue.state}
                        </span>
                      </p>
                      <p>
                        <strong className="text-gray-700">ZIP:</strong>{" "}
                        <span className="text-gray-600">
                          {selectedVenue.zipcode}
                        </span>
                      </p>

                      {selectedVenue.country && (
                        <p>
                          <strong className="text-gray-700">Country:</strong>{" "}
                          <span className="text-gray-600">
                            {selectedVenue.country}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>

                  {selectedVenue.screen_size && (
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-900 mb-3">
                        Screen Details
                      </h3>

                      <div className="space-y-2 text-sm">
                        <p>
                          <strong className="text-gray-700">Size:</strong>{" "}
                          <span className="text-gray-600">
                            {selectedVenue.screen_size}
                          </span>
                        </p>

                        {selectedVenue.screen_orientation && (
                          <p>
                            <strong className="text-gray-700">
                              Orientation:
                            </strong>{" "}
                            <span className="text-gray-600">
                              {selectedVenue.screen_orientation}
                            </span>
                          </p>
                        )}

                        {selectedVenue.pixel_width &&
                          selectedVenue.pixel_height && (
                            <p>
                              <strong className="text-gray-700">
                                Resolution:
                              </strong>{" "}
                              <span className="text-gray-600">
                                {selectedVenue.pixel_width} ×{" "}
                                {selectedVenue.pixel_height}
                              </span>
                            </p>
                          )}

                        {selectedVenue.screen_position && (
                          <p>
                            <strong className="text-gray-700">Position:</strong>{" "}
                            <span className="text-gray-600">
                              {selectedVenue.screen_position}
                            </span>
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Expandable Additional Details */}
                {!showAllDetails && (
                  <Button
                    variant="outline"
                    onClick={() => setShowAllDetails(true)}
                    className="w-full"
                  >
                    Show More Details
                  </Button>
                )}

                {showAllDetails && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-2">
                          Venue ID
                        </h3>
                        <p className="text-sm text-gray-600 font-mono">
                          {selectedVenue.venue_name}
                        </p>
                      </div>

                      {selectedVenue.dma_cma && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="font-semibold text-gray-900 mb-2">
                            DMA/CMA
                          </h3>
                          <p className="text-sm text-gray-600">
                            {selectedVenue.dma_cma}
                          </p>
                        </div>
                      )}

                      <div className="bg-green-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-2">
                          Coordinates
                        </h3>
                        <div className="space-y-1 text-sm">
                          <p>
                            <strong className="text-gray-700">Lat:</strong>{" "}
                            <span className="text-gray-600">
                              {selectedVenue.latitude}
                            </span>
                          </p>
                          <p>
                            <strong className="text-gray-700">Lng:</strong>{" "}
                            <span className="text-gray-600">
                              {selectedVenue.longitude}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      onClick={() => setShowAllDetails(false)}
                      className="w-full"
                    >
                      Show Less
                    </Button>
                  </>
                )}

                <Button
                  onClick={() => {
                    onViewOnMap?.(selectedVenue);
                    onOpenChange(false); // close modal
                  }}
                  className="w-full"
                  size="lg"
                >
                  <MapPinned size={20} className="mr-2" />
                  View on Map
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox overlay (separate component with high z-index) */}
      <ImageLightbox
        open={lightboxOpen}
        imageUrl={selectedVenue?.image_url || null}
        alt={selectedVenue?.venue_name || "Venue image"}
        zoomLevel={zoomLevel}
        setZoomLevel={setZoomLevel}
        onClose={closeLightbox}
      />
    </>
  );
}